<template>
  <b-container
    class="locations-by-txntype-config"
    data-cy="locations-by-txntype-config"
  >
    <!-- Loading -->
    <loading-spinner
      v-if="loading"
      data-cy="locations-by-txntype-loading-spinner"
    >
      <div slot="title">
        Please wait, loading Square payment location settings
      </div>
    </loading-spinner>

    <!-- Load error / retry -->
    <b-row v-if="loadError && !loading">
      <b-col>
        <div
          class="load-error"
          data-cy="locations-by-txntype-load-error"
        >
          <font-awesome-icon
            icon="triangle-exclamation"
            size="2x"
            class="icon"
            fixed-width
            color="red"
          />
          <div class="message">
            Could not load the location settings.
            <span class="detail">{{ loadError }}</span>
          </div>
          <b-button
            variant="primary"
            data-cy="locations-by-txntype-retry"
            @click="load"
          >
            Retry
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Loaded -->
    <template v-if="!loading && !loadError">
      <b-row>
        <b-col>
          <div class="config-header">
            <h3>Square Payment Locations by Transaction Type</h3>
            <p class="subtitle">
              Choose a Square location for each transaction type, or leave it as
              Square Default to use your main Dashboard location.
            </p>
          </div>
        </b-col>
      </b-row>

      <!-- Validation error banner -->
      <b-row v-if="saveError">
        <b-col>
          <div
            class="banner banner-error"
            data-cy="locations-by-txntype-save-error"
          >
            <font-awesome-icon
              icon="circle-exclamation"
              class="banner-icon"
            />
            <div class="banner-content">
              <template v-if="saveError.invalidLocationIds.length">
                Save failed &mdash; {{ saveError.invalidLocationIds.length }}
                invalid location(s). One of your selected locations is no longer
                active at Square. Fix it below and try again.
              </template>
              <template v-else>
                {{ saveError.message }}
              </template>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- No transaction types for this site -->
      <b-row v-if="!hasTxnTypes">
        <b-col>
          <div
            class="empty-placeholder"
            data-cy="locations-by-txntype-no-txntypes"
          >
            No transaction types are available for this site.
          </div>
        </b-col>
      </b-row>

      <!-- Transaction-type table -->
      <b-row v-if="hasTxnTypes">
        <b-col>
          <table
            class="mapping-table"
            data-cy="locations-by-txntype-table"
          >
            <thead>
              <tr>
                <th>Transaction Type</th>
                <th>Square Location</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.txntype"
                class="mapping-row"
                :class="{ 'row-error': rowHasError(row) }"
                :data-cy="`mapping-row-${row.txntype}`"
              >
                <!-- Transaction Type cell -->
                <td class="txntype-cell">
                  <span class="txntype-label">{{ row.label }}</span>
                  <span class="txntype-key">{{ row.txntype }}</span>
                </td>

                <!-- Square Location cell -->
                <td class="location-cell">
                  <template v-if="!readOnly">
                    <b-form-select
                      v-model="row.location_id"
                      :data-cy="`location-select-${row.txntype}`"
                      :options="rowLocationOptions(row)"
                      @change="onRowChange"
                    />
                  </template>
                  <template v-else>
                    <template v-if="isSentinel(row)">
                      <span class="use-default">Square Default</span>
                    </template>
                    <template v-else>
                      <span class="location-name">{{ locationName(row.location_id) }}</span>
                      <span class="location-id">{{ row.location_id }}</span>
                    </template>
                  </template>
                  <div
                    v-if="rowHasError(row)"
                    class="row-error-detail"
                  >
                    Location {{ row.location_id }} was not found at Square.
                    It may have been archived or removed. Pick another location.
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </b-col>
      </b-row>

      <!-- Bottom bar: dirty indicator + cancel/save -->
      <b-row v-if="!readOnly && hasTxnTypes">
        <b-col>
          <div class="config-actions">
            <span
              v-if="dirty"
              class="dirty-indicator"
              data-cy="locations-by-txntype-dirty"
            >
              <span class="dot" /> Unsaved changes
            </span>
            <span class="spacer" />
            <b-button
              variant="outline-secondary"
              :disabled="saving || !dirty"
              data-cy="locations-by-txntype-cancel"
              @click="cancel"
            >
              Cancel
            </b-button>
            <b-button
              variant="success"
              :disabled="saving || !dirty"
              data-cy="locations-by-txntype-save"
              @click="save"
            >
              {{ saving ? 'Saving…' : 'Save' }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
const SQUARE_DEFAULT_LABEL = 'Square Default'

export default {
  name: 'locations-by-txntype-config',
  props: {
    // Site whose mapping to manage. All API calls derive the active site/JWT/
    // endpoint from the host-provided store (paymentAPICall); siteid is required
    // by the spec and surfaced to the host for context / permission gating.
    siteid: {
      type: String,
      required: true
    },
    // Render read-only: no location selectors or save/cancel controls (§4.6).
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      loading: true,
      loadError: null,
      saving: false,
      saveError: null,
      rows: [],
      originalMap: {}
    }
  },
  computed: {
    locations: function() {
      return this.$store.state.square.locations || []
    },
    transactionTypes: function() {
      return this.$store.state.square.transactionTypes || {}
    },
    locationsById: function() {
      return this.locations.reduce((acc, loc) => {
        acc[loc.id] = loc
        return acc
      }, {})
    },
    hasTxnTypes: function() {
      return this.rows.length > 0
    },
    // Base location dropdown options: Square Default first, then every location.
    locationSelectOptions: function() {
      const opts = [{ value: '', text: SQUARE_DEFAULT_LABEL }]
      this.locations.forEach(loc => {
        opts.push({
          value: loc.id,
          text: `${loc.name} — ${loc.id} · ${loc.status}`
        })
      })
      return opts
    },
    // The persisted map derived from the working rows: only rows with a chosen
    // (non-default) location are kept. Square Default rows are omitted entirely
    // (never persisted as "").
    workingMap: function() {
      return this.rows.reduce((acc, row) => {
        if (row.location_id) {
          acc[row.txntype] = row.location_id
        }
        return acc
      }, {})
    },
    dirty: function() {
      return !this.mapsEqual(this.workingMap, this.originalMap)
    }
  },
  watch: {
    // @dirty fires on each clean<->dirty transition (§3.4).
    dirty: function(newVal) {
      this.$emit('dirty', newVal)
    }
  },
  mounted: function() {
    this.load()
  },
  methods: {
    load: function() {
      this.loading = true
      this.loadError = null
      this.saveError = null
      // Two fetches in parallel; render only once both resolve (§3.3). The
      // config route returns the mapping and the available transaction types
      // together.
      Promise.all([
        this.$store.dispatch('square/loadLocationsByTxnType'),
        this.$store.dispatch('square/loadLocations', { force: true })
      ])
        .then(([{ map }]) => {
          this.originalMap = { ...map }
          this.rows = this.buildRows(map)
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          this.loadError = err.message || 'Unknown error'
          this.$emit('error', err)
          console.error('LocationsByTxnTypeConfig load failed:', err)
        })
    },
    // One row per available transaction type, carrying its current mapping
    // (or '' for Square Default). Sorted by label.
    buildRows: function(map) {
      return Object.keys(this.transactionTypes)
        .map(txntype => ({
          txntype: txntype,
          label: (this.transactionTypes[txntype] && this.transactionTypes[txntype].label) || txntype,
          location_id: map[txntype] || ''
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    mapsEqual: function(a, b) {
      const ka = Object.keys(a).sort()
      const kb = Object.keys(b).sort()
      if (ka.length !== kb.length) return false
      return ka.every((k, i) => kb[i] === k && a[k] === b[k])
    },
    isSentinel: function(row) {
      return !row.location_id
    },
    locationName: function(locationId) {
      const loc = this.locationsById[locationId]
      return loc ? loc.name : locationId
    },
    // A row references a location_id that is not in the current /locations
    // response — stale on load and flagged after a failed save.
    isStale: function(row) {
      return !!row.location_id && !this.locationsById[row.location_id]
    },
    rowHasError: function(row) {
      if (this.isStale(row)) return true
      return !!this.saveError && this.saveError.invalidLocationIds.includes(row.location_id)
    },
    // Per-row options: the shared list, plus the row's own location if it is no
    // longer a known Square location (so the stale value stays visible).
    rowLocationOptions: function(row) {
      if (row.location_id && !this.locationsById[row.location_id]) {
        return this.locationSelectOptions.concat([{
          value: row.location_id,
          text: `${row.location_id} (not found at Square)`
        }])
      }
      return this.locationSelectOptions
    },
    onRowChange: function() {
      // Changing a selection clears a prior save error so the banner doesn't linger.
      this.saveError = null
    },
    cancel: function() {
      this.rows = this.buildRows(this.originalMap)
      this.saveError = null
    },
    save: function() {
      this.saving = true
      this.saveError = null
      this.$store.dispatch('square/saveLocationsByTxnType', { map: this.workingMap })
        .then(saved => {
          this.originalMap = { ...saved }
          this.rows = this.buildRows(saved)
          this.saving = false
          this.$emit('saved', saved)
        })
        .catch(err => {
          this.saving = false
          const invalidLocationIds = this.rows
            .filter(r => this.isStale(r))
            .map(r => r.location_id)
          this.saveError = {
            message: err.message || 'Save failed. Please try again.',
            invalidLocationIds: invalidLocationIds
          }
          this.$emit('error', err)
          console.error('LocationsByTxnTypeConfig save failed:', err)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
  .locations-by-txntype-config {

    .config-header {
      margin-bottom: 1rem;

      h3 {
        margin: 0 0 0.25rem;
        font-size: 1.4rem;
        color: #333;
      }

      .subtitle {
        margin: 0;
        color: #666;
      }
    }

    .load-error {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 1rem;
      padding: 1.5rem;

      .message {
        flex: 1;

        .detail {
          display: block;
          color: #888;
          font-size: 0.85rem;
        }
      }
    }

    .banner {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      padding: 0.75rem 1rem;
      margin-bottom: 1rem;
      border-radius: 4px;
      line-height: 1.4;

      .banner-icon {
        margin-right: 0.6rem;
        flex-shrink: 0;
        margin-top: 0.15rem;
      }

      .banner-content {
        flex: 1;
      }
    }

    .banner-error {
      background-color: #f8d7da;
      border: 1px solid #f5c2c7;
      border-left: 4px solid #dc3545;
      color: #842029;

      .banner-icon {
        color: #dc3545;
      }
    }

    .mapping-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 1rem;

      th,
      td {
        text-align: left;
        padding: 0.6rem 0.75rem;
        border-bottom: 1px solid #dee2e6;
        vertical-align: top;
      }

      th {
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #666;
      }

      .mapping-row.row-error {
        td {
          background-color: #fff5f5;
        }
        outline: 2px solid #dc3545;

        .location-name,
        .location-id {
          color: #842029;
        }
      }

      .txntype-label {
        display: block;
        font-weight: 600;
      }

      .txntype-key {
        display: block;
        font-family: monospace;
        font-size: 0.8rem;
        color: #888;
      }

      .location-name {
        display: block;
        font-weight: 600;
      }

      .location-id {
        display: block;
        font-family: monospace;
        font-size: 0.8rem;
        color: #888;
      }

      .use-default {
        font-style: italic;
        color: #888;
      }

      .row-error-detail {
        margin-top: 0.35rem;
        color: #dc3545;
        font-size: 0.85rem;
      }
    }

    .empty-placeholder {
      padding: 2rem;
      margin-bottom: 1rem;
      text-align: center;
      color: #888;
      border: 2px dashed #ced4da;
      border-radius: 0.5rem;
    }

    .config-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding-top: 1rem;
      border-top: 1px solid #dee2e6;

      .spacer {
        flex: 1;
      }

      .dirty-indicator {
        color: #997404;

        .dot {
          display: inline-block;
          width: 0.6rem;
          height: 0.6rem;
          border-radius: 50%;
          background-color: #ffc107;
          margin-right: 0.25rem;
        }
      }
    }
  }
</style>
